import DefaultList from "@/components/DefaultList";
import {
  PATH_PROJECT_CONTRACTOR_VISIT,
  DATETIME_FORMAT,
  NO_DATA,
  Module,
} from "@/constants";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ContractorVisitsService } from "@/modules/projects/services/contractorVisits";
import {
  basicListingContractorVisit,
  fullListingContractorVisit,
  VISIT_STATUS,
  DEVICE_TYPE,
} from "@/schemas/contractorVisit";
import { SETTLEMENT_TYPE } from "@/schemas/commonFields/settlementType";
import { PROJECT_STATUS } from "@/schemas/project";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { Row } from "@tanstack/react-table";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { exists } from "i18next";
import { DateTime } from "luxon";
import { ArrowRightFromLine, ArrowLeftFromLine } from "lucide-react";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";

export const ContractorsVisitsDirectory = memo(() => {
  const { i18n } = useTranslation();
  const permissions = useModulePermissions(Module.ContractorVisits);
  const name = "projectContractorVisits";

  const schema = permissions.contractor_visits_read_full
    ? fullListingContractorVisit
    : basicListingContractorVisit;

  const columnOverrides = useMemo(
    () => ({
      status: {
        id: "status",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          const key = `projectContractorVisits.enum.status.${row.original.status}`;
          if (!exists(key)) {
            return row.original.status;
          }
          return i18n.t(key);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: VISIT_STATUS,
              translationKey: "projectContractorVisits.enum.status",
            },
          },
        },
      },
      device_type: {
        id: "device_type",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          if (!row.original.device_type) {
            return NO_DATA;
          }
          const key = `projectContractorVisits.enum.deviceType.${row.original.device_type}`;
          if (!exists(key)) {
            return row.original.device_type;
          }
          return i18n.t(key);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: DEVICE_TYPE,
              translationKey: "projectContractorVisits.enum.deviceType",
            },
          },
        },
      },
      settlement_type: {
        id: "settlement_type",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          const key = `settlementType.${row.original.settlement_type}`;
          if (!exists(key)) {
            return row.original.settlement_type;
          }
          return i18n.t(key);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: SETTLEMENT_TYPE,
              translationKey: "settlementType",
            },
          },
        },
      },
      ...(permissions.contractor_visits_read_full && {
        start_datetime: {
          id: "start_datetime",
          cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
            const datetime = row.original.start_datetime;
            if (!datetime) return NO_DATA;
            const dt = DateTime.fromISO(datetime);
            if (!dt.isValid) return datetime;
            return (
              <div className="flex items-center gap-2">
                <ArrowRightFromLine className="w-2 h-2 text-gray-500" />
                {dt.toFormat(DATETIME_FORMAT)}
              </div>
            );
          },
        },
        end_datetime: {
          id: "end_datetime",
          cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
            const datetime = row.original.end_datetime;
            if (!datetime) return NO_DATA;
            const dt = DateTime.fromISO(datetime);
            if (!dt.isValid) return datetime;
            return (
              <div className="flex items-center gap-2">
                {dt.toFormat(DATETIME_FORMAT)}
                <ArrowLeftFromLine className="w-2 h-2 text-gray-500" />
              </div>
            );
          },
        },
      }),
      start_geolocation_accuracy: {
        id: "start_geolocation_accuracy",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          if (!row.original.start_geolocation_accuracy) return NO_DATA;
          return i18n.t(
            `contractorVisits.geolocationAccuracy.${row.original.start_geolocation_accuracy}`,
          );
        },
      },
      confirmation_geolocation_accuracy: {
        id: "confirmation_geolocation_accuracy",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          if (!row.original.confirmation_geolocation_accuracy) return NO_DATA;
          return i18n.t(
            `contractorVisits.geolocationAccuracy.${row.original.confirmation_geolocation_accuracy}`,
          );
        },
      },
      project_status: {
        id: "project_status",
        cell: ({ row }: { row: Row<ReadContractorVisit> }) => {
          if (!row.original.project_status) {
            return NO_DATA;
          }
          return i18n.t(`projectStatuses.${row.original.project_status}`);
        },
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_STATUS,
              translationKey: "projectStatuses",
            },
          },
        },
      },
    }),
    [i18n, permissions.contractor_visits_read_full],
  );

  const initialColumnVisibility = useMemo(
    () => ({
      client_ref_number: false,
      client_name: false,
      client_tax_number: false,
      invoicing_company: false,
      payroll_type: false,
      project_type: false,
      store_ref_number: false,
      network: false,
      city: false,
      street_name: false,
      street_number: false,
      postal_code: false,
      contractor_full_name_and_personal_id_number: false,
      contractor_phone_number: false,
      suggested_start_time: false,
      suggested_end_time: false,
      geolocalization_enabled: false,
      editable_by_pr: false,
      hours_editable_by_contractor: false,
      confirmable_by_contractor: false,
      edit_allowed: false,
      project_status: false,
    }),
    [],
  );

  return (
    <DefaultList<ReadContractorVisit>
      schema={schema}
      name={name}
      fetchList={ContractorVisitsService.getList}
      pathBuilder={(row) =>
        generatePath(PATH_PROJECT_CONTRACTOR_VISIT, {
          contractorVisitId: row.id,
        })
      }
      columnOverrides={columnOverrides}
      tableFeatures={{
        exportSettings: {
          moduleName: Module.ContractorVisits,
        },
        initialColumnVisibility,
      }}
    />
  );
});
